import api from './api';

/**
 * Mongoose discriminator keys — must match backend VALID_STUDENT_TYPES.
 */
export const STUDENT_TYPES = {
  SECONDARY: 'SecondaryStudent',
  PROFESSIONAL: 'ProfessionalStudent',
};

export const VALID_STUDENT_TYPES = Object.values(STUDENT_TYPES);

/** Secondary trades (must match backend constants/trades.js). */
export const SECONDARY_TRADES = ['NIT', 'SOD', 'ETEL', 'CSA'];

/** Professional training courses (must match backend enum if applicable). */
export const PROFESSIONAL_COURSES = [
  'CCTV CAMERA',
  'SATELLITE ENGINEERING',
  'BIOMETRIC ACCESS CONTROL',
  'SOFTWARE DEVELOPMENT',
  'NETWORKING',
  'IOT',
];

/** Secondary TVET students — fixed fee per term (RWF). */
export const SECONDARY_TERM_FEE = 30000;

/** Professional course fees in RWF (update amounts here when pricing changes). */
export const PROFESSIONAL_COURSE_FEES = {
  'CCTV CAMERA': 250000,
  'SATELLITE ENGINEERING': 250000,
  'BIOMETRIC ACCESS CONTROL': 250000,
  'SOFTWARE DEVELOPMENT': 300000,
  'NETWORKING': 300000,
};

export const formatRwf = (amount) =>
  typeof amount === 'number' ? amount.toLocaleString('en-US') : '';

/**
 * Resolve display + numeric fee for sidebar and payment flows.
 * @param {string} studentType - SecondaryStudent | ProfessionalStudent
 * @param {string|string[]} [course] - Professional course name(s) when applicable
 */
export const getTrainingFee = (studentType, course) => {
  const paymentNote =
    'Payment is due after registration. Bank transfer and mobile money accepted.';

  if (studentType === STUDENT_TYPES.SECONDARY) {
    return {
      amount: SECONDARY_TERM_FEE,
      amountDisplay: formatRwf(SECONDARY_TERM_FEE),
      subtitle: 'RWF per term',
      badge: 'Training Fee',
      note: paymentNote,
      isPerCourse: false,
    };
  }

  if (studentType === STUDENT_TYPES.PROFESSIONAL) {
    const courseList = PROFESSIONAL_COURSES.map((name) => ({
      name,
      amount: PROFESSIONAL_COURSE_FEES[name],
    }));

    const selectedCourses = Array.isArray(course) ? course : course ? [course] : [];
    if (selectedCourses.length > 0 && selectedCourses.every((name) => PROFESSIONAL_COURSE_FEES[name] != null)) {
      const amount = selectedCourses.reduce((total, name) => total + PROFESSIONAL_COURSE_FEES[name], 0);
      return {
        amount,
        amountDisplay: formatRwf(amount),
        subtitle: 'RWF',
        courseLabel: selectedCourses.join(' + '),
        badge: 'Course Fee',
        note: `Fee for ${selectedCourses.join(', ')}. ${paymentNote}`,
        isPerCourse: true,
        courseList,
      };
    }

    return {
      amount: null,
      amountDisplay: null,
      subtitle: 'varies by course',
      badge: 'Course Fees',
      note: `Select a course above to see your fee. ${paymentNote}`,
      isPerCourse: true,
      courseList,
    };
  }

  return {
    amount: SECONDARY_TERM_FEE,
    amountDisplay: formatRwf(SECONDARY_TERM_FEE),
    subtitle: 'RWF per term',
    badge: 'Training Fee',
    note: paymentNote,
    isPerCourse: false,
  };
};

const omitEmpty = (obj) => {
  const out = {};
  Object.entries(obj).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    if (typeof value === 'object' && !Array.isArray(value) && !(value instanceof Date)) {
      const nested = omitEmpty(value);
      if (Object.keys(nested).length > 0) out[key] = nested;
    } else {
      out[key] = value;
    }
  });
  return out;
};

/**
 * Build POST body for discriminator-based student creation.
 */
export const buildCreatePayload = (formData) => {
  const studentType = formData.studentType ?? STUDENT_TYPES.SECONDARY;

  if (!studentType) {
    const err = new Error('studentType is required. Use "SecondaryStudent" or "ProfessionalStudent".');
    err.error = err.message;
    throw err;
  }
  if (!VALID_STUDENT_TYPES.includes(studentType)) {
    const err = new Error(
      `Invalid studentType "${studentType}". Use "SecondaryStudent" or "ProfessionalStudent".`
    );
    err.error = err.message;
    throw err;
  }

  const emergencyContact = omitEmpty({
    name: formData.emergencyContact?.name?.trim(),
    phone: formData.emergencyContact?.phone?.trim(),
    relation: formData.emergencyContact?.relation?.trim(),
  });

  const batchId = formData.batch
    ? (typeof formData.batch === 'object' ? formData.batch._id : formData.batch)
    : formData.batchId || undefined;

  const base = omitEmpty({
    studentType,
    fullName: formData.fullName?.trim(),
    gender: formData.gender?.trim(),
    dateOfBirth: formData.dateOfBirth || undefined,
    phone: formData.phone?.trim(),
    email: formData.email?.trim(),
    address: formData.address?.trim(),
    // Backend create/update resolve on `batchId` (admin cohort assignment)
    batchId: batchId || undefined,
    emergencyContact: Object.keys(emergencyContact).length ? emergencyContact : undefined,
  });

  if (studentType === STUDENT_TYPES.SECONDARY) {
    return omitEmpty({
      ...base,
      fieldOfStudy: formData.fieldOfStudy?.trim(),
      schoolName: formData.schoolName?.trim(),
      level: formData.level?.trim(),
    });
  }

  if (studentType === STUDENT_TYPES.PROFESSIONAL) {
    const years = formData.yearsOfExperience;
    const yearsNum =
      years !== '' && years != null && !Number.isNaN(Number(years)) ? Number(years) : 0;
    const today = new Date();
    const defaultEnd = new Date(today);
    defaultEnd.setMonth(defaultEnd.getMonth() + 6);
    const experienceParts = [formData.profession?.trim(), formData.employer?.trim()].filter(
      Boolean
    );

    const selectedCourses = Array.isArray(formData.courses)
      ? formData.courses.map((course) => course?.trim()).filter(Boolean)
      : formData.course?.trim()
        ? [formData.course.trim()]
        : [];

    return omitEmpty({
      ...base,
      courses: selectedCourses.length > 0 ? selectedCourses : undefined,
      startDate: formData.startDate || today.toISOString().split('T')[0],
      expectedEndDate: formData.expectedEndDate || defaultEnd.toISOString().split('T')[0],
      hasPriorExperience: yearsNum > 0,
      experienceDetails:
        experienceParts.length > 0
          ? experienceParts.join(' — ')
          : yearsNum > 0
            ? `${yearsNum} year(s) experience`
            : undefined,
    });
  }

  return base;
};

/**
 * Student Service
 * Handles all student-related API calls
 */

const studentService = {
  /**
   * Get all students
   * @returns {Promise<Array>} - List of students
   */
  getAllStudents: async () => {
    try {
      const response = await api.get('/students');
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Get student by ID
   * @param {string} id - Student ID
   * @returns {Promise<Object>} - Student object
   */
  getStudentById: async (id) => {
    try {
      const response = await api.get(`/students/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Check if a student already exists by full name, email, or phone (for duplicate prevention).
   * No student with the same name may be registered twice. Also checks email and phone.
   * Returns the existing student if found, null otherwise. May throw if list endpoint is not available (e.g. auth required).
   * @param {string} fullName - Full name to check
   * @param {string} email - Email to check
   * @param {string} phone - Phone to check
   * @returns {Promise<Object|null>} - Existing student or null
   */
  findExistingByEmailOrPhone: async (fullName, email, phone) => {
    const list = await studentService.getAllStudents();
    const norm = (v) => (v && typeof v === 'string' ? v.trim().toLowerCase() : '');
    const normName = (v) => (v && typeof v === 'string' ? v.trim().toLowerCase().replace(/\s+/g, ' ') : '');
    const normPhone = (v) => (v && typeof v === 'string' ? v.replace(/\D/g, '').replace(/^0+/, '') || v.replace(/\D/g, '') : '');
    const name = normName(fullName);
    const e = norm(email);
    const p = normPhone(phone);
    const found = list.find(
      (s) =>
        (name && normName(s.fullName) === name) ||
        (e && norm(s.email) === e) ||
        (p && normPhone(s.phone) === p)
    );
    return found || null;
  },

  /**
   * Create new student
   * @param {Object} studentData - Student data
   * @returns {Promise<Object>} - Created student object
   */
  createStudent: async (studentData) => {
    try {
      const body = buildCreatePayload(studentData);
      const response = await api.post('/students', body);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Update student
   * @param {string} id - Student ID
   * @param {Object} studentData - Student data to update
   * @returns {Promise<Object>} - Updated student object
   */
  updateStudent: async (id, studentData) => {
    try {
      const response = await api.put(`/students/${id}`, studentData);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Delete student
   * @param {string} id - Student ID
   * @returns {Promise<Object>} - Deletion confirmation message
   */
  deleteStudent: async (id) => {
    try {
      const response = await api.delete(`/students/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Search students by name or other criteria
   * @param {string} searchTerm - Search term
   * @returns {Promise<Array>} - Filtered list of students
   */
  searchStudents: async (searchTerm) => {
    try {
      const students = await studentService.getAllStudents();
      return students.filter(student =>
        student.fullName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        student.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        student.fieldOfStudy?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        student.phone?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    } catch (error) {
      throw error;
    }
  },

  /**
   * Get students by field of study
   * @param {string} field - Field of study
   * @returns {Promise<Array>} - List of students in that field
   */
  getStudentsByField: async (field) => {
    try {
      const students = await studentService.getAllStudents();
      return students.filter(student =>
        student.fieldOfStudy?.toLowerCase() === field.toLowerCase()
      );
    } catch (error) {
      throw error;
    }
  },

  /**
   * Get students by level
   * @param {string} level - Education level
   * @returns {Promise<Array>} - List of students at that level
   */
  getStudentsByLevel: async (level) => {
    try {
      const students = await studentService.getAllStudents();
      return students.filter(student =>
        student.level?.toLowerCase() === level.toLowerCase()
      );
    } catch (error) {
      throw error;
    }
  },
};

export default studentService;
